import {
  ORDER_TIMEFRAMES,
  type OrderFilter,
  type OrderPracticeFilter,
  type OrderStatusFilter,
  type OrderTimeframeFilter,
  searchLeads as coreSearchLeads,
} from '@witmetrics/unison-core';
import type { DateRange } from '@witmetrics/utils';
import { includesKeyValue, mapToId } from '@witmetrics/utils/arrays';
import {
  COMPARISON_OPERATORS,
  FILTER_TYPES,
  ORDER_STATUSES,
} from '@witmetrics/utils/constants';
import { isEmpty } from '@witmetrics/utils/misc';
import { isNumber } from '@witmetrics/utils/numbers';
import { isString } from '@witmetrics/utils/strings';
import { API } from '@/api';
import { CUSTOMER, ORDER_STATUS } from '@/dialogs/OrderFilterDialog/constants';

export const searchLeads = (query: string, pageSize = 25) =>
  coreSearchLeads(API, query, pageSize);

export function getInitialFilters(params: URLSearchParams): OrderFilter[] {
  const filters = params.get('filters');
  if (!filters) return [];
  return parseFilters(filters).filter(isValidFilter);
}

function parseFilters(filters: string): Partial<OrderFilter>[] {
  try {
    const parsed = JSON.parse(filters);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isCompleteFilter(
  filter: Partial<OrderFilter>
): filter is OrderFilter {
  return (
    !isEmpty(filter.filterType) &&
    !isEmpty(filter.comparisonOperator) &&
    !isEmpty(filter.column) &&
    !isEmpty(filter.value)
  );
}

export function isValidFilter(
  filter: Partial<OrderFilter>
): filter is OrderFilter {
  if (!isCompleteFilter(filter)) return false;
  if (filter.filterType !== FILTER_TYPES.COLUMN_VALUE) return false;
  if (
    !includesKeyValue(
      Object.values(COMPARISON_OPERATORS),
      'value',
      filter.comparisonOperator
    )
  ) {
    return false;
  }
  if (filter.column === ORDER_STATUS.value) {
    return isValidStatusValue(filter.value);
  }
  if (filter.column === CUSTOMER.value) {
    return isValidPracticeValue(filter.value);
  }
  return isTimeframeFilter(filter) && isValidTimeframeValue(filter.value);
}

export function isTimeframeFilter(
  filter: Partial<OrderFilter>
): filter is Partial<OrderTimeframeFilter> {
  return (
    filter.column === ORDER_TIMEFRAMES.CREATED_AT.value ||
    filter.column === ORDER_TIMEFRAMES.UPDATED_AT.value
  );
}

export function isDateRange(value: unknown): value is DateRange {
  return Array.isArray(value);
}

function isValidStatusValue(value: OrderStatusFilter['value']) {
  const statusIds = mapToId(Object.values(ORDER_STATUSES));
  return toArray(value).every((id) => statusIds.includes(id));
}

function isValidPracticeValue(value: OrderPracticeFilter['value']) {
  return toArray(value).every(isNumber);
}

function isValidTimeframeValue(value: OrderTimeframeFilter['value']) {
  return isString(value) || (isDateRange(value) && value.every(isString));
}

function toArray<T>(value: T | T[]) {
  return Array.isArray(value) ? value : [value];
}
